import React, { useMemo, useState } from 'react';
import {
  Tabs,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Switch,
  Button,
  Upload,
  Space,
  Card,
  Typography,
  Alert,
  message
} from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import adminAxios from './adminAxios';
import { resolveFrontendApiBase } from '../utils/apiBase';
import './AdminPromoCodes.css';

const { TextArea } = Input;
const { Text } = Typography;

const API_BASE = resolveFrontendApiBase();

type ActionType = 'Forever Credits';

function normalizeCode(s: string): string {
  return (s || '').replace(/\s+/g, '').toUpperCase();
}

function isValidCodeFormat(code: string): boolean {
  return /^[A-Z0-9_-]{3,64}$/.test(code);
}

function parseExpiry(d?: Dayjs | null): string | null {
  if (!d) return null;
  // Send ISO string the backend will parse to UTC
  return d.toISOString();
}

const AdminPromoCodes: React.FC = () => {
  // Single-create form
  const [singleForm] = Form.useForm();
  const [singleLoading, setSingleLoading] = useState<boolean>(false);
  const [singleResult, setSingleResult] = useState<string>('');

  // Bulk form
  const [bulkForm] = Form.useForm();
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [rawFileName, setRawFileName] = useState<string>('');
  const [parsedCodes, setParsedCodes] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<any | null>(null);

  const uniqueCodes = useMemo(() => {
    const uniq = Array.from(new Set(parsedCodes));
    return uniq.filter(c => !!c);
  }, [parsedCodes]);

  const duplicateCountInFile = parsedCodes.length - uniqueCodes.length;
  const bulkValidCodeCount = uniqueCodes.filter(c => isValidCodeFormat(c)).length;

  const copyResult = async (value: string) => {
    try {
      await navigator.clipboard?.writeText(value);
      message.success('Result copied');
    } catch {
      message.error('Unable to copy result');
    }
  };

  const handleSingleSubmit = async () => {
    try {
      const values = await singleForm.validateFields();
      const payload = {
        code: normalizeCode(values.code),
        action: values.action as ActionType,
        amount: Number(values.amount),
        description: values.description || '',
        expires_at: parseExpiry(values.expiresAt ?? null),
        max_redemptions: Number(values.maxRedemptions ?? 1),
        overwrite: !!values.overwrite,
        requires_new_account: !!values.requiresNewAccount,
      };

      if (!payload.code) {
        message.error('Code is required');
        return;
      }
      if (!isValidCodeFormat(payload.code)) {
        message.error('Code format invalid. Use A-Z, 0-9, underscore or dash, 3-64 chars.');
        return;
      }
      if (!payload.amount || payload.amount <= 0) {
        message.error('Amount must be a positive integer');
        return;
      }
      if (payload.action !== 'Forever Credits') {
        message.error('Only "Forever Credits" is supported at this time');
        return;
      }

      setSingleLoading(true);
      setSingleResult('');
      const res = await adminAxios.post(`${API_BASE}/admin/promo/create`, payload);
      setSingleResult(JSON.stringify(res.data, null, 2));
      message.success('Promo code saved');
    } catch (e: any) {
      if (e?.errorFields) return; // form validation errors
      const detail = e?.response?.data?.detail || e?.message || 'Failed to save promo code';
      setSingleResult(detail);
      message.error(detail);
    } finally {
      setSingleLoading(false);
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      setRawFileName(file.name);
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const text = (ev?.target?.result as string) || '';
          const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
          let codes: string[] = [];
          if (lines.length) {
            const first = lines[0].toLowerCase();
            const hasHeader = first.includes('code') && first.split(',').length === 1;
            const dataLines = hasHeader ? lines.slice(1) : lines;
            codes = dataLines.map(normalizeCode).filter(Boolean);
          }
          setParsedCodes(codes);
          message.success(`Parsed ${codes.length} codes from "${file.name}"`);
        } catch (err) {
          console.error(err);
          message.error('Failed to parse file');
          setParsedCodes([]);
        }
      };
      reader.readAsText(file);
      // prevent auto upload
      return false;
    },
    maxCount: 1,
    accept: '.csv,.txt'
  };

  const handleBulkSubmit = async () => {
    try {
      const values = await bulkForm.validateFields();
      const codesToSend = uniqueCodes.filter(c => isValidCodeFormat(c));
      if (!codesToSend.length) {
        message.error('No valid codes to upload');
        return;
      }

      const payload = {
        codes: codesToSend,
        action: values.action as ActionType,
        amount: Number(values.amount),
        description: values.description || '',
        expires_at: parseExpiry(values.expiresAt ?? null),
        max_redemptions: Number(values.maxRedemptions ?? 1),
        overwrite: !!values.overwrite,
        requires_new_account: !!values.requiresNewAccount,
      };

      if (!payload.amount || payload.amount <= 0) {
        message.error('Amount must be a positive integer');
        return;
      }
      if (payload.action !== 'Forever Credits') {
        message.error('Only "Forever Credits" is supported at this time');
        return;
      }

      setBulkLoading(true);
      setBulkResult(null);
      const res = await adminAxios.post(`${API_BASE}/admin/promo/bulk`, payload, { timeout: 60000 });
      setBulkResult(res.data);
      message.success('Bulk operation complete');
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data?.detail || e?.message || 'Bulk upload failed';
      setBulkResult({ success: false, error: detail });
      message.error(detail);
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="admin-promo-codes">
      <div className="admin-promo-codes__header">
        <div>
          <span className="admin-promo-codes__kicker">Admin workflow</span>
          <h3>Promo Codes</h3>
        </div>
        <Space className="admin-promo-codes__header-meta" wrap>
          <Text type="secondary">Single and bulk forever-credit codes</Text>
        </Space>
      </div>

      <div className="admin-promo-codes__overview">
        <div className="admin-promo-codes__metric">
          <span>Parsed</span>
          <strong>{parsedCodes.length}</strong>
        </div>
        <div className="admin-promo-codes__metric">
          <span>Unique</span>
          <strong>{uniqueCodes.length}</strong>
        </div>
        <div className="admin-promo-codes__metric">
          <span>Valid</span>
          <strong>{bulkValidCodeCount}</strong>
        </div>
        <div className="admin-promo-codes__metric">
          <span>Duplicates</span>
          <strong>{duplicateCountInFile}</strong>
        </div>
      </div>

      <Tabs
        className="admin-promo-codes__tabs"
        defaultActiveKey="single"
        items={[
          {
            key: 'single',
            label: 'Single Code',
            children: (
              <div className="admin-promo-codes__task-stack">
          <Card className="admin-promo-codes__task" size="small" title="Create Single Code">
            <Form
              form={singleForm}
              layout="vertical"
              initialValues={{
                action: 'Forever Credits',
                amount: 1000,
                maxRedemptions: 1,
                overwrite: false,
                requiresNewAccount: false
              }}
            >
              <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Code is required' }]}>
                <Input placeholder="e.g., WELCOME5000" />
              </Form.Item>

              <Form.Item name="action" label="Action" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Forever Credits">Forever Credits</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Enter amount' }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="description" label="Description (optional)">
                <TextArea rows={3} placeholder="Optional description shown to user upon validation" />
              </Form.Item>

              <Form.Item name="expiresAt" label="Expires At (optional)">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="maxRedemptions" label="Max Redemptions (default 1)">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="overwrite" label="Overwrite if exists" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item
                name="requiresNewAccount"
                label="Limit to new accounts (created in last 72h)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" onClick={handleSingleSubmit} loading={singleLoading}>
                    Save Code
                  </Button>
                  <Button onClick={() => { singleForm.resetFields(); setSingleResult(''); }}>
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>

          {singleResult && (
            <Card
              className="admin-promo-codes__result"
              size="small"
              title="Result"
              extra={<Button size="small" onClick={() => copyResult(singleResult)}>Copy</Button>}
            >
              <pre>{singleResult}</pre>
            </Card>
          )}
              </div>
            ),
          },
          {
            key: 'bulk',
            label: 'Bulk Upload (CSV)',
            children: (
              <div className="admin-promo-codes__task-stack">
          <Card className="admin-promo-codes__task" size="small" title="Bulk Upload Codes">
            <Form
              form={bulkForm}
              layout="vertical"
              initialValues={{
                action: 'Forever Credits',
                amount: 1000,
                maxRedemptions: 1,
                overwrite: false,
                requiresNewAccount: false
              }}
            >
              <Form.Item label="CSV File (one code per line, optional header 'code')">
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
                {rawFileName && (
                  <Text type="secondary">Selected: {rawFileName}</Text>
                )}
              </Form.Item>

              <Alert
                className="admin-promo-codes__parse-summary"
                type="info"
                showIcon
                message="CSV Parsing Summary"
                description={
                  <div>
                    <div>Total lines parsed: {parsedCodes.length}</div>
                    <div>Duplicate lines (within file): {duplicateCountInFile}</div>
                    <div>Unique codes to send: {uniqueCodes.length}</div>
                  </div>
                }
              />

              <Form.Item name="action" label="Action" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Forever Credits">Forever Credits</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Enter amount' }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="description" label="Description (optional)">
                <TextArea rows={3} placeholder="Optional description shown to user upon validation" />
              </Form.Item>

              <Form.Item name="expiresAt" label="Expires At (optional)">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="maxRedemptions" label="Max Redemptions (default 1)">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="overwrite" label="Overwrite if exists" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item
                name="requiresNewAccount"
                label="Limit to new accounts (created in last 72h)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    onClick={handleBulkSubmit}
                    loading={bulkLoading}
                    disabled={uniqueCodes.length === 0}
                  >
                    Upload Codes
                  </Button>
                  <Button
                    onClick={() => {
                      setParsedCodes([]);
                      setRawFileName('');
                      setBulkResult(null);
                      bulkForm.resetFields();
                    }}
                  >
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>

          {bulkResult && (
            <Card
              className="admin-promo-codes__result"
              size="small"
              title="Bulk Result"
              extra={<Button size="small" onClick={() => copyResult(JSON.stringify(bulkResult, null, 2))}>Copy</Button>}
            >
              <pre>{JSON.stringify(bulkResult, null, 2)}</pre>
            </Card>
          )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default AdminPromoCodes;
